import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsApi, Product } from '../services/api';
import { TextField, List, ListItem, ListItemText, Paper, ListItemAvatar, Avatar, Box } from '@mui/material';

const SearchBar: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.trim()) {
        try {
          const response = await productsApi.getAll();
          const allProducts = response.data.data.products;
          const filteredProducts = allProducts.filter((product: Product) =>
            product.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          setSuggestions(filteredProducts);
          setIsOpen(true);
        } catch (err) {
          console.error('Failed to fetch product suggestions:', err);
          setSuggestions([]);
          setIsOpen(false);
        }
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    };

    // Add debounce to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    // Handle clicks outside of the search component
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleSuggestionClick = (productId: number) => {
    setIsOpen(false);
    setSearchTerm('');
    navigate(`/product/${productId}`);
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: '800px', // Increased max width
        margin: '0 auto',
        padding: '16px',
      }}
    >
      <div ref={searchRef} style={{ position: 'relative', width: '100%' }}>
        <TextField
          label="Search Products"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              '&:hover': {
                '& > fieldset': {
                  borderColor: '#1976d2',
                }
              }
            },
            '& .MuiOutlinedInput-input': {
              padding: '14px 16px',
              fontSize: '1.1rem',
            }
          }}
        />
        {isOpen && suggestions.length > 0 && (
          <Paper 
            elevation={3} 
            sx={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: '400px',
              overflowY: 'auto',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#ffffff',
            }}
          >
            <List>
              {suggestions.map((product) => (
                <ListItem 
                  button 
                  key={product.id} 
                  onClick={() => handleSuggestionClick(product.id)}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    padding: '12px 16px',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar 
                      src={product.imageUrl} 
                      alt={product.name}
                      sx={{ 
                        width: 48, 
                        height: 48,
                        marginRight: '12px'
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={product.name}
                    secondary={`₹${product.price}`}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '1rem',
                        fontWeight: 500,
                      },
                      '& .MuiListItemText-secondary': {
                        fontSize: '0.9rem',
                        color: '#1976d2',
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </div>
    </Box>
  );
};

export default SearchBar; 